import { Filters, SortKey, defaultFilters } from "@/hooks/useComparador";
import { megapixelOptions, typeOptions, CameraType } from "@/data/cameras";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X, SlidersHorizontal } from "lucide-react";
import { useLang } from "@/i18n/LangContext";

interface FilterPanelProps {
  filters: Filters;
  updateFilter: <K extends keyof Filters>(key: K, value: Filters[K]) => void;
  resetFilters: () => void;
  sortKey: SortKey;
  setSortKey: (k: SortKey) => void;
  totalShowing: number;
  totalAll: number;
}

function ToggleBadge({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button onClick={onClick} className={`text-xs px-2.5 py-1 rounded-full border transition-colors cursor-pointer ${
      active ? "bg-primary text-primary-foreground border-primary"
             : "bg-transparent text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
    }`}>{children}</button>
  );
}

function TriStateButton({ label, value, onChange, activeColor }: {
  label: string; value: boolean | null; onChange: (v: boolean | null) => void; activeColor?: string;
}) {
  const cycle = () => {
    if (value === null) onChange(true);
    else if (value === true) onChange(false);
    else onChange(null);
  };
  const icon = value === true ? "✓" : value === false ? "✗" : "—";
  const cls = value === true
    ? (activeColor || "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700")
    : value === false
    ? "bg-destructive/10 text-destructive border-destructive/30"
    : "bg-transparent text-muted-foreground border-border hover:border-primary/50";
  return (
    <button onClick={cycle} className={`text-xs px-2.5 py-1 rounded-full border transition-colors cursor-pointer flex items-center gap-1 ${cls}`}>
      <span className="font-mono w-3 text-center">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

export function FilterPanel({ filters, updateFilter, resetFilters, sortKey, setSortKey, totalShowing, totalAll }: FilterPanelProps) {
  const { t } = useLang();
  const isFiltered = JSON.stringify(filters) !== JSON.stringify(defaultFilters);

  const toggleMP = (mp: number) => {
    const arr = filters.megapixels.includes(mp) ? filters.megapixels.filter(x => x !== mp) : [...filters.megapixels, mp];
    updateFilter("megapixels", arr);
  };
  const toggleType = (t: CameraType) => {
    const arr = filters.type.includes(t) ? filters.type.filter(x => x !== t) : [...filters.type, t];
    updateFilter("type", arr);
  };

  return (
    <aside className="bg-sidebar border-r border-sidebar-border h-full overflow-y-auto flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-sidebar-border sticky top-0 bg-sidebar z-10">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold text-sidebar-foreground">{t("filters")}</span>
          {isFiltered && <Badge className="text-xs h-4 px-1.5 bg-primary text-primary-foreground">{t("filterActive")}</Badge>}
        </div>
        <span className="text-xs text-muted-foreground font-medium">
          <span className="text-primary">{totalShowing}</span>/{totalAll}
        </span>
      </div>

      <div className="px-4 py-4 flex flex-col gap-5 flex-1">
        {/* Marca */}
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">{t("brand")}</Label>
          <div className="flex flex-wrap gap-1.5">
            {(["Todas", "DAHUA", "TVT"] as const).map(b => (
              <ToggleBadge key={b} active={filters.brand === b} onClick={() => updateFilter("brand", b)}>
                {b === "Todas" ? t("brandAll") : b}
              </ToggleBadge>
            ))}
          </div>
        </div>

        {/* Megapixels */}
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">{t("megapixels")}</Label>
          <div className="flex flex-wrap gap-1.5">
            {megapixelOptions.map(mp => (
              <ToggleBadge key={mp} active={filters.megapixels.includes(mp)} onClick={() => toggleMP(mp)}>{mp}MP</ToggleBadge>
            ))}
          </div>
        </div>

        {/* Tipo */}
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">{t("type")}</Label>
          <div className="flex flex-wrap gap-1.5">
            {typeOptions.map(tp => (
              <ToggleBadge key={tp} active={filters.type.includes(tp)} onClick={() => toggleType(tp)}>{tp}</ToggleBadge>
            ))}
          </div>
        </div>

        {/* Lente */}
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">{t("lensType")}</Label>
          <div className="flex flex-wrap gap-1.5">
            {(["Todas", "Fixa", "Motorizada"] as const).map(l => (
              <ToggleBadge key={l} active={filters.lensType === l} onClick={() => updateFilter("lensType", l)}>
                {l === "Todas" ? t("lensAll") : l === "Fixa" ? t("lensFixed") : t("lensMotorized")}
              </ToggleBadge>
            ))}
          </div>
        </div>

        {/* IR */}
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">{t("irMin")}</Label>
          <div className="flex flex-wrap gap-1.5">
            {[0, 20, 30, 50, 80].map(ir => (
              <ToggleBadge key={ir} active={filters.irMin === ir} onClick={() => updateFilter("irMin", ir)}>
                {ir === 0 ? t("irAll") : `≥${ir}m`}
              </ToggleBadge>
            ))}
          </div>
        </div>

        {/* WDR */}
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">{t("wdr")}</Label>
          <div className="flex flex-wrap gap-1.5">
            <ToggleBadge active={filters.wdrType === null} onClick={() => updateFilter("wdrType", null)}>{t("wdrAll")}</ToggleBadge>
            <ToggleBadge active={filters.wdrType === "WDR"} onClick={() => updateFilter("wdrType", "WDR")}>
              WDR (True)
            </ToggleBadge>
            <ToggleBadge active={filters.wdrType === "DWDR"} onClick={() => updateFilter("wdrType", "DWDR")}>
              DWDR (Digital)
            </ToggleBadge>
          </div>
        </div>

        {/* Deteção */}
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">{t("intelligentDetection")}</Label>
          <div className="flex flex-col gap-1.5">
            <TriStateButton
              label={t("detPeople")}
              value={filters.detPeople}
              onChange={v => updateFilter("detPeople", v)}
              activeColor="bg-indigo-100 text-indigo-700 border-indigo-300 dark:bg-indigo-900/40 dark:text-indigo-300 dark:border-indigo-700"
            />
            <TriStateButton
              label={t("detPeopleVehicle")}
              value={filters.detVehicle}
              onChange={v => updateFilter("detVehicle", v)}
              activeColor="bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700"
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">{t("triStateLegend")}</p>
        </div>

        {/* Funcionalidades */}
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">{t("features")}</Label>
          <div className="flex flex-wrap gap-1.5">
            <TriStateButton label="WiFi" value={filters.hasWifi} onChange={v => updateFilter("hasWifi", v)} />
            <TriStateButton label="4G" value={filters.has4G} onChange={v => updateFilter("has4G", v)} />
            <TriStateButton label="Solar" value={filters.hasSolar} onChange={v => updateFilter("hasSolar", v)} activeColor="bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700" />
            <TriStateButton label={t("audio")} value={filters.hasAudio} onChange={v => updateFilter("hasAudio", v)} />
            <TriStateButton label="Full Color" value={filters.hasColorNight} onChange={v => updateFilter("hasColorNight", v)} />
            <TriStateButton label="Dual Light" value={filters.hasDualLight} onChange={v => updateFilter("hasDualLight", v)} />
            <TriStateButton label="IK10" value={filters.hasIK10} onChange={v => updateFilter("hasIK10", v)} />
            <TriStateButton label={t("lpr")} value={filters.hasLPR} onChange={v => updateFilter("hasLPR", v)} activeColor="bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700" />
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">{t("triStateLegend")}</p>
        </div>

        {/* Ordenar */}
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">{t("sortBy")}</Label>
          <div className="flex flex-wrap gap-1.5">
            {([
              { key: "model", label: t("sortModel") },
              { key: "megapixels", label: t("sortMp") },
              { key: "irRange", label: t("sortIr") },
              { key: "price", label: t("sortPrice") },
              { key: "brand", label: t("sortBrand") },
            ] as const).map(s => (
              <ToggleBadge key={s.key} active={sortKey === s.key} onClick={() => setSortKey(s.key)}>{s.label}</ToggleBadge>
            ))}
          </div>
        </div>
      </div>

      {isFiltered && (
        <div className="px-4 pb-4 sticky bottom-0 bg-sidebar pt-2 border-t border-sidebar-border">
          <Button variant="outline" size="sm" className="w-full gap-2" onClick={resetFilters}>
            <X className="w-3.5 h-3.5" /> {t("clearFilters")}
          </Button>
        </div>
      )}
    </aside>
  );
}
